import React, {useState} from 'react';
import {
    Box,
    Card,
    CardMedia,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Radio,
    Stack,
    Switch,
    TextField, ThemeProvider,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EditIcon from '@mui/icons-material/Edit';
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import {Option, PictureChoiceQuestion} from "crqlar-react-common/dist/models/forms/form";
import {getScaledUrl, MediaSize} from "crqlar-react-common/dist/utils/media.util";
import {theme} from "booking-engine-widget/src/theme";
import ImagePicker from "crqlar-react-common/dist/components/ImagePicker";
import {useTranslation} from "react-i18next";

interface PictureChoiceInputProps {
    question: PictureChoiceQuestion;
    onQuestionUpdate: (question: PictureChoiceQuestion) => void;
    isSelected?: boolean;
    onSelect?: () => void;
}

const generateId = () => Math.random().toString(36).substring(2, 15);

const PictureChoiceInput: React.FC<PictureChoiceInputProps> = ({
                                                                   question,
                                                                   onQuestionUpdate,
                                                               isSelected = false,
                                                               onSelect
                                                           }) => {
    const {t} = useTranslation();
    const [editingImageOptionId, setEditingImageOptionId] = useState<string | null>(null);

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            title: e.target.value
        });
    };

    const handleDescriptionChange = (value?: string) => {
        onQuestionUpdate({
            ...question,
            description: value || ''
        });
    };

    const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            required: e.target.checked
        });
    };

    const handleOptionChange = (optionId: string, newLabel: string) => {
        const updatedOptions = (question.options || []).map(option =>
            option.id === optionId ? {...option, label: newLabel} : option
        );
        onQuestionUpdate({
            ...question,
            options: updatedOptions
        });
    };

    const handleOptionImageChange = (optionId: string, media: any) => {
        const updatedOptions = (question.options || []).map(option => {
            if (option.id === optionId) {
                return {
                    ...option,
                    imageMedia: media
                };
            }
            return option;
        });
        onQuestionUpdate({
            ...question,
            options: updatedOptions
        });
        // Close the dialog after selecting/removing image
        setEditingImageOptionId(null);
    };

    const handleAddOption = () => {
        const newOption: Option = {
            id: generateId(),
            label: t('Option {{number}}', {number: (question.options?.length || 0) + 1})
        };
        onQuestionUpdate({
            ...question,
            options: [...(question.options || []), newOption]
        });
    };

    const handleDeleteOption = (optionId: string) => {
        const updatedOptions = (question.options || []).filter(option => option.id !== optionId);
        onQuestionUpdate({
            ...question,
            options: updatedOptions
        });
    };

    const handleToggleMultiple = () => {
        onQuestionUpdate({
            ...question,
            allowMultiple: !question.allowMultiple
        });
    };

    const options = question.options || [];

    return (
        <Stack
            onClick={onSelect}
            sx={{
                p: 3,
                borderRadius: 1,
                border: isSelected ? '2px solid' : '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: 'primary.light',
                    backgroundColor: 'action.hover'
                },
                position: 'relative'
            }}
        >
            <Box sx={{alignSelf: 'flex-end'}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.required || false}
                            onChange={handleRequiredChange}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                        />
                    }
                    label={<Typography variant="caption">{t('Required')}</Typography>}
                    labelPlacement="start"
                    onClick={(e) => e.stopPropagation()}
                    sx={{m: 0}}
                />
            </Box>

            <TextField
                fullWidth
                label={t('Question Label')}
                value={question.title}
                onChange={handleTitleChange}
                variant="standard"
                sx={{mb: 2, pr: 12}}
                onClick={(e) => e.stopPropagation()}
            />

            {isSelected ? (
                <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
                    <MdEditor
                        value={question.description || ''}
                        onChange={handleDescriptionChange}
                        placeholder={t('Question Description (optional)')}
                        className={'mini-mdx'}
                    />
                </Box>
            ) : (
                question.description && (
                    <Box sx={{mb: 2}}>
                        <MdRenderer>
                            {question.description}
                        </MdRenderer>
                    </Box>
                )
            )}

            {isSelected && (
                <Box sx={{mb: 2}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={question.allowMultiple || false}
                                onChange={handleToggleMultiple}
                                onClick={(e) => e.stopPropagation()}
                            />
                        }
                        label={t('Allow multiple selections')}
                    />
                </Box>
            )}

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 2,
                mb: 1
            }}>
                {options.map((option, index) => (
                    <Box key={option.id}>
                        <Card
                            sx={{
                                position: 'relative',
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': {
                                    borderColor: 'primary.light'
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    cursor: isSelected ? 'pointer' : 'default',
                                    position: 'relative'
                                }}
                            >
                                <ImagePicker
                                    image={option.imageMedia}
                                    setImage={image => handleOptionImageChange(option.id, image)}
                                    height={120}
                                    width={'100%'}
                                />
                            </Box>

                            <Box sx={{p: 1, display: 'flex', alignItems: 'center'}}>
                                {question.allowMultiple ? (
                                    <Checkbox disabled size="small"/>
                                ) : (
                                    <Radio disabled size="small"/>
                                )}
                                {isSelected ? (
                                    <TextField
                                        fullWidth
                                        value={option.label}
                                        onChange={(e) => handleOptionChange(option.id, e.target.value)}
                                        variant="standard"
                                        size="small"
                                        placeholder={t('Option {{number}}', {number: index + 1})}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ml: 0.5}}
                                    />
                                ) : (
                                    <Typography variant="body2" sx={{ml: 0.5}}>
                                        {option.label}
                                    </Typography>
                                )}
                            </Box>

                            {isSelected && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    display: 'flex',
                                    gap: 0.5
                                }}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteOption(option.id);
                                        }}
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            '&:hover': {backgroundColor: 'rgba(255, 255, 255, 1)'}
                                        }}
                                    >
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </Box>
                            )}
                        </Card>
                    </Box>
                ))}
            </Box>

            {isSelected && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'primary.main',
                        cursor: 'pointer',
                        mt: 2,
                        '&:hover': {opacity: 0.8}
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleAddOption();
                    }}
                >
                    <AddIcon fontSize="small" sx={{mr: 0.5}}/>
                    <Typography variant="body2">{t('Add option')}</Typography>
                </Box>
            )}

            {/* Image Picker Dialog */}
            <ThemeProvider theme={theme}>
                <Dialog
                    open={!!editingImageOptionId}
                    onClose={() => setEditingImageOptionId(null)}
                    maxWidth="md"
                    fullWidth
                    onClick={(e) => e.stopPropagation()}
                >
                    <DialogTitle>
                        {t('Edit Option Image')}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{pt: 2}}>

                            {/*<MediaPickerField*/}
                            {/*    value={*/}
                            {/*        editingImageOptionId*/}
                            {/*            ? question.options?.find(opt => opt.id === editingImageOptionId)?.imageMedia*/}
                            {/*            : undefined*/}
                            {/*    }*/}
                            {/*    onChange={(media) => {*/}
                            {/*        if (editingImageOptionId) {*/}
                            {/*            handleOptionImageChange(editingImageOptionId, media);*/}
                            {/*        }*/}
                            {/*    }}*/}
                            {/*    mediaType="image"*/}
                            {/*    usageType={MediaUsageType.GENERAL}*/}
                            {/*    height={300}*/}
                            {/*    maxWidth="100%"*/}
                            {/*/>*/}
                        </Box>
                    </DialogContent>
                </Dialog>
            </ThemeProvider>

        </Stack>
    );
};

export default PictureChoiceInput;
